/**
 * Typed failures of the RoadSense live-payload fetch, so the screen can show
 * "not found", "still working" or "retry" copy. Kept free of React and `@src`
 * imports so jest can test it directly.
 */
export class RoadSenseReportNotFoundError extends Error {
  readonly kind = "not_found" as const;
  constructor(message = "Report not found") {
    super(message);
    this.name = "RoadSenseReportNotFoundError";
  }
}

export class RoadSenseReportNotReadyError extends Error {
  readonly kind = "not_ready" as const;
  readonly status: "generating" | "failed";
  constructor(status: "generating" | "failed", message = "Report not ready") {
    super(message);
    this.name = "RoadSenseReportNotReadyError";
    this.status = status;
  }
}

/**
 * 403 maps to not-found: the server answers 403 for another tenant's report so
 * ids can't be probed, but to this tenant it simply doesn't exist. 409 means
 * the report has never completed a generation.
 */
export async function liveResponseError(response: Response): Promise<Error | undefined> {
  if (response.ok) return undefined;
  if (response.status === 404 || response.status === 403) {
    return new RoadSenseReportNotFoundError();
  }
  if (response.status === 409) {
    return new RoadSenseReportNotReadyError(await readNotReadyStatus(response));
  }
  const text = await response.text().catch(() => "");
  return new Error(text || `Live payload request failed: ${String(response.status)}`);
}

async function readNotReadyStatus(response: Response): Promise<"generating" | "failed"> {
  try {
    const body: unknown = await response.json();
    const status =
      typeof body === "object" && body !== null && "details" in body ? readStatus(body.details) : undefined;
    if (status !== undefined) return status;
  } catch {
    // Body wasn't JSON. Fall through to the safer "still working" copy.
  }
  return "generating";
}

function readStatus(details: unknown): "generating" | "failed" | undefined {
  if (typeof details !== "object" || details === null || !("status" in details)) return undefined;
  return details.status === "generating" || details.status === "failed" ? details.status : undefined;
}

/** A failed background poll keeps the last good report on screen rather than showing the error. */
export function visibleReportError(state: { hasReport: boolean; error: Error | null }): Error | null {
  return state.hasReport ? null : state.error;
}
